import { ArrowRightIcon, RotateCcwIcon } from 'lucide-react';
import type { LifecycleStage } from '../../../data/referenceGuides/types';

/**
 * Numbered stages, each with steps and a "who drives this phase" footnote.
 *
 * The note supports a single **bolded** lead clause, which is how every packet
 * writes it ("**ERP drives this phase** — MRP calculates what, when, how much").
 * Parsed rather than stored as HTML so the data files stay plain strings.
 */
function EmphasisedNote({
  note
}: {
  note: string;
}) {
  const match = note.match(/^\*\*(.+?)\*\*(.*)$/s);
  if (!match) return <>{note}</>;
  return <>
      <span className="text-white/85 font-medium" data-id="element-277" data-mp="1bq9vm5wo9gm6">{match[1]}</span>
      {match[2]}
    </>;
}
export function LifecycleBlock({
  stages
}: {
  stages: LifecycleStage[];
}) {
  return <div className="space-y-4" data-id="element-278" data-mp="bmnlu3k1o8d">
      {stages.map(stage => {
      const success = stage.noteEmphasis === 'success';
      return <section key={stage.label + stage.title} className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden" data-id="element-279" data-mp="3rho1eaewmvxp">
            <div className="px-6 py-3 bg-white/[0.03] border-b border-white/[0.08] flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-280" data-mp="12rdrv4lzuil5">
              <span className="text-xs font-bold uppercase tracking-widest text-violet-link" data-id="element-281" data-mp="16q4pdcudg28s">
                {stage.label}
              </span>
              <h3 className="text-white/85 text-base font-semibold" data-id="element-282" data-mp="xqq4qu0vylw7">{stage.title}</h3>
            </div>

            <ol className="p-6 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-6 list-none" data-id="element-283" data-mp="j1tv4sj6l0th">
              {stage.steps.map((step, i) => <li key={step.n} className="relative md:pr-6" data-id="element-284" data-mp="nuwgwehpv5z4" data-mpi={i}>
                  <div className="flex items-start gap-3" data-id="element-285" data-mp="a2zv0s6n5sb6">
                    <span className={`w-7 h-7 rounded-full ${success ? 'bg-guide-procurement/15 text-guide-procurement' : 'bg-violet-link/15 text-violet-link'} text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-286" data-mp="a0dpey3b9mvl">
                      {step.n}
                    </span>
                    <div data-id="element-287" data-mp="54ybk65azqz6">
                      <p className="text-white text-sm font-semibold mb-1" data-id="element-288" data-mp="3i4ek56jrd73v">{step.title}</p>
                      <p className="text-white/55 text-sm leading-relaxed" data-id="element-289" data-mp="3ddg2o3n1a2z1">{step.body}</p>
                    </div>
                  </div>
                  {i < stage.steps.length - 1 && <ArrowRightIcon aria-hidden="true" className="hidden md:block absolute right-0 top-1 w-4 h-4 text-white/25" data-id="element-290" data-mp="38bz2ybgacmk1" />}
                </li>)}
            </ol>

            <div className={`px-6 py-3.5 border-t border-white/[0.08] ${success ? 'bg-guide-procurement/[0.06]' : 'bg-violet-link/[0.05]'} flex items-start gap-2.5`} data-id="element-291" data-mp="4wbe3cs84xl2">
              <RotateCcwIcon aria-hidden="true" className={`w-4 h-4 flex-shrink-0 mt-0.5 ${success ? 'text-guide-procurement' : 'text-violet-link'}`} data-id="element-292" data-mp="3r4b5ay6xvnu6" />
              <p className="text-white/60 text-sm leading-relaxed" data-id="element-293" data-mp="3m587nytagjw8">
                <EmphasisedNote note={stage.note} data-id="element-294" data-mp="2fqh0xehhi4ua" />
              </p>
            </div>
          </section>;
    })}
    </div>;
}